import { useCallback, useEffect, useState } from 'react';
import {
  dispatch,
  ErrorVariants,
  getClockInInfo,
  getUserData,
  getWorkspaceId,
  showToast,
  TripLocation,
  updateClockInInfo,
  useAppSelector,
  useTripClockinMutation,
  useTripClockoutMutation,
} from '@zonofi/zono-money-store';
import { isUndefined } from 'lodash';
import { useCurrentLocation } from './use-current-location';
import { useNetworkContext } from '../network';

interface ClockInModalProps {
  open: boolean;
  onClose: () => void;
}

export const useClockInLogic = (props: ClockInModalProps) => {
  const { open, onClose } = props;
  const clockInInfo = useAppSelector(getClockInInfo);
  const workspaceId = useAppSelector(getWorkspaceId);
  const userData = useAppSelector(getUserData);

  const [tripClockIn, tripClockInResponse] = useTripClockinMutation();
  const [tripClockOut, tripClockOutResponse] = useTripClockoutMutation();
  const [successModalState, setSuccessModalState] = useState<{
    show: boolean;
    type?: 'CLOCK-IN' | 'CLOCK-OUT' | undefined;
    data?: {
      clockIn: string;
      clockOut?: string;
    };
  }>({
    show: false,
    type: undefined,
    data: {
      clockIn: '',
      clockOut: undefined,
    },
  });

  const { networkStatus: isOnline } = useNetworkContext();
  const { currentLocation } = useCurrentLocation({
    shouldFetch: open,
    isOnline,
  });

  const handleSuccess = (
    type: 'CLOCK-IN' | 'CLOCK-OUT',
    updateData?: {
      tripId: string;
      clockIn: string;
      clockOut?: string;
      clockInLocation?: TripLocation;
    }
  ) => {
    if (updateData && updateData?.tripId) {
      dispatch(updateClockInInfo(updateData));
    } else {
      dispatch(updateClockInInfo(undefined));
    }

    setSuccessModalState({
      show: true,
      type,
      data: {
        clockIn: updateData?.clockIn || '',
        clockOut: updateData?.clockOut,
      },
    });
  };

  const onClockInOutPress = useCallback(() => {
    const params = { sellerWorkspaceId: workspaceId };

    if (clockInInfo && clockInInfo?.tripId) {
      // Clock-Out
      tripClockOut({
        params,
        body: {
          tripId: clockInInfo.tripId,
          clockOutLocation: {
            lat: currentLocation?.lat || '',
            lng: currentLocation?.lng || '',
            address: currentLocation?.formatted_address,
          },
        },
      });
      return;
    } else if (isUndefined(clockInInfo)) {
      // Clock-In
      tripClockIn({
        params,
        body: {
          teamMemberId: userData?.inviteId ?? '',
          clockInLocation: {
            lat: currentLocation?.lat || '',
            lng: currentLocation?.lng || '',
            address: currentLocation?.formatted_address,
          },
        },
      });
    }
  }, [clockInInfo, currentLocation]);

  useEffect(() => {
    if (tripClockInResponse.isSuccess && tripClockInResponse.data) {
      const data = tripClockInResponse.data;
      handleSuccess('CLOCK-IN', {
        tripId: data?.id,
        clockIn: data?.clockInTime || new Date().toISOString(),
        clockInLocation: data?.clockInLocation,
      });
      return;
    } else if (tripClockInResponse.isError) {
      dispatch(
        showToast({
          message: 'Sorry, Not able to Clock-in',
          type: ErrorVariants.WARNING,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }
  }, [tripClockInResponse]);

  useEffect(() => {
    if (tripClockOutResponse.isSuccess && tripClockOutResponse.data) {
      const data = tripClockOutResponse.data;
      handleSuccess('CLOCK-OUT', {
        tripId: '',
        clockIn: '',
        clockOut: data?.clockOutTime || new Date().toISOString(),
      });
      return;
    } else if (tripClockOutResponse.isError) {
      dispatch(
        showToast({
          message: 'Sorry, Not able to Clock-out',
          type: ErrorVariants.WARNING,
          canDisplay: true,
          responseStatus: 'Error',
        })
      );
    }
  }, [tripClockOutResponse]);

  const buttonLoading = clockInInfo?.tripId
    ? tripClockOutResponse?.isLoading
    : tripClockInResponse?.isLoading;

  return {
    isOnline,
    clockInInfo,
    currentLocation,
    onClockInOutPress,
    buttonLoading,
    successModalState,
    setSuccessModalState,
  };
};
